import React, { useEffect, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';

type PullToRefreshProps = {
  children: React.ReactNode;
  onRefresh?: () => Promise<void> | void;
};

const THRESHOLD = 70;
const MAX_DISTANCE = 120;
const SPINNER_SIZE = 24;
const RESISTANCE = 0.55;

/** Walk up the DOM from el to find the nearest scrollable ancestor */
const findScrollableParent = (el: Element | null): Element | null => {
  if (!el || el === document.documentElement) return document.documentElement;
  const style = window.getComputedStyle(el);
  const overflow = style.overflowY;
  if ((overflow === 'auto' || overflow === 'scroll') && el.scrollHeight > el.clientHeight) {
    return el;
  }
  return findScrollableParent(el.parentElement);
};

const PullToRefresh: React.FC<PullToRefreshProps> = ({ children, onRefresh }) => {
  const startY = useRef<number | null>(null);
  const pulling = useRef(false);
  const wasScrolling = useRef(false);
  const scrollCooldown = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [canRefresh, setCanRefresh] = useState(false);

  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => {
      if (refreshing) return;
      // Find the actual scrollable container from the touch target
      const scrollable = findScrollableParent(e.target as Element);
      const scrollTop = scrollable?.scrollTop ?? 0;
      // Only activate when at the very top AND not in momentum scroll cooldown
      if (scrollTop === 0 && !wasScrolling.current) {
        startY.current = e.touches[0].clientY;
      } else {
        startY.current = null;
      }
    };

    const onScroll = () => {
      // Mark as scrolling; clear PTR eligibility for 400ms after scroll stops
      wasScrolling.current = true;
      if (scrollCooldown.current) clearTimeout(scrollCooldown.current);
      scrollCooldown.current = setTimeout(() => {
        wasScrolling.current = false;
      }, 400);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startY.current === null || refreshing) return;
      const raw = e.touches[0].clientY - startY.current;
      if (raw <= 0) return;
      e.preventDefault();
      pulling.current = true;
      const dampened = raw * RESISTANCE;
      const capped = Math.min(dampened, MAX_DISTANCE);
      setDistance(capped);
      setCanRefresh(capped >= THRESHOLD);
    };

    const triggerRefresh = async () => {
      setRefreshing(true);
      setDistance(THRESHOLD);
      try {
        if (onRefresh) {
          await onRefresh();
        } else {
          await new Promise((r) => setTimeout(r, 800));
          window.location.reload();
          return;
        }
      } finally {
        setRefreshing(false);
        setDistance(0);
        setCanRefresh(false);
      }
    };

    const reset = () => {
      setDistance(0);
      setCanRefresh(false);
      pulling.current = false;
      startY.current = null;
    };

    const onTouchEnd = () => {
      if (!pulling.current) {
        reset();
        return;
      }
      if (canRefresh) {
        void triggerRefresh();
      } else {
        reset();
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    // Capture scroll from any container (useCapture=true catches inner div scroll too)
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('scroll', onScroll, { capture: true });
      if (scrollCooldown.current) clearTimeout(scrollCooldown.current);
    };
  }, [onRefresh, refreshing, canRefresh]);

  const visible = refreshing || distance > 0;
  const scale = Math.min(distance / THRESHOLD, 1);

  const spinnerStyle: React.CSSProperties = {
    position: 'fixed',
    left: '50%',
    top: 0,
    transform: `translate(-50%, ${refreshing ? THRESHOLD : distance}px) scale(${scale})`,
    transition: refreshing ? 'transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)' : 'transform 0ms',
    zIndex: 1400,
    opacity: visible ? 1 : 0,
    pointerEvents: 'none',
  };

  return (
    <>
      <div style={spinnerStyle} aria-hidden={!visible}>
        <CircularProgress size={SPINNER_SIZE} />
      </div>
      {children}
    </>
  );
};

export default PullToRefresh;
